import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getWaterAnalysisInfo } from '../../../Utils/analysis/waterAnalysis';
import moment from 'moment';
import { BiError } from 'react-icons/bi';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import Loading from '../../Shared/Loading';
import { useSelector } from 'react-redux';
import { exportTableToExcel } from '../../../Helpers/Excel/export';
import excelPng from '../../../assets/images/excel.png';


const ViewWaterAnalysis = () => {
    const { exploitationId, analysisId } = useParams();
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(true);
    const userRole = useSelector((state) => state.user.userRole.name);
    const userInfo = useSelector((state) => state.user.userInfo);


    const userLink = () => {
        if (userRole === 'client') {
            return '/client';
        } else if (userRole === 'admin' || userRole === 'superadmin') {
            return '/admin';
        } else if (userRole === 'domain_manager') {
            return '/domain-manager';
        } else if (userRole === 'exploitation_manager') {
            return '/exploitation-manager';
        }
    };

    useEffect(() => {
        fetchAnalysisDetails();
    }, [analysisId]);

    const fetchAnalysisDetails = async () => {
        try {
            const details = await getWaterAnalysisInfo(analysisId);
            setAnalysisData(details.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des détails de l\'analyse:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleExport = () => {
        const info = [
            'Analyse d\'Eau',
            `Date de réalisation: ${moment(analysisData.date_realisation).format('DD/MM/YYYY')}`,
            `Laboratoire: ${analysisData.laboratoire}`,
            `EC (µS/cm): ${analysisData.ec}`,
            `pH: ${analysisData.ph}`,
            `Échantillon: ${analysisData.echantillon}`,
        ];

        const titles = ['Paramètres de l\'eau'];
        const tableIDs = ['waterAnalysisTable'];
        const filename = `Analyse_Eau_${analysisId}`;
        const user = `${userInfo.first_name} ${userInfo.last_name}`;

        exportTableToExcel(info, tableIDs, filename, titles, null, user);
    }



    const waterParameters = [
        { key: 'cation', name: 'Cations', isHeader: true },
        { key: 'sodium', name: 'Sodium', symbol: 'Na⁺' },
        { key: 'potassium', name: 'Potassium', symbol: 'K⁺' },
        { key: 'magnesium', name: 'Magnésium', symbol: 'Mg²⁺' },
        { key: 'calcium', name: 'Calcium', symbol: 'Ca²⁺' },
        { key: 'ammonium', name: 'Ammonium', symbol: 'NH₄⁺' },
        { key: 'anion', name: 'Anions', isHeader: true },
        { key: 'chlorures', name: 'Chlorures', symbol: 'Cl⁻' },
        { key: 'sulfates', name: 'Sulfates', symbol: 'SO₄²⁻' },
        { key: 'carbonates', name: 'Carbonates', symbol: 'CO₃²⁻' },
        { key: 'bicarbonates', name: 'Bicarbonates', symbol: 'HCO₃⁻' },
        { key: 'nitrates', name: 'Nitrates', symbol: 'NO₃⁻' },
        { key: 'dihydrogenophosphates', name: 'Dihydrogénophosphates', symbol: 'H₂PO₄⁻' },
        { key: 'oligoElements', name: 'Oligo-éléments', isHeader: true },
        { key: 'cuivre', name: 'Cuivre', symbol: 'Cu²⁺' },
        { key: 'zinc', name: 'Zinc', symbol: 'Zn²⁺' },
        { key: 'fer', name: 'Fer', symbol: 'Fe²⁺/Fe³⁺' },
        { key: 'manganese', name: 'Manganèse', symbol: 'Mn²⁺' },
        { key: 'bore', name: 'Bore', symbol: 'B' },
    ];





    return (
        <div className="bg-white h-full">
            <Header title="Détails de l'Analyse d'Eau" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`${userLink()}/exploitation/${exploitationId}/analysis/eau`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />
                    <div className="font-semibold text-base text-indigo-600">Retour</div>
                </Link>
                {loading ? <Loading /> :
                    !analysisData ? (
                        <div className='flex items-center flex-col text-xl mt-10 '>
                            <BiError className='text-red-500 text-5xl' />
                            Aucune donnée trouvée
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-end mb-4">
                                <div
                                    className="flex gap-3 border p-2 hover:bg-green-200 rounded-md transition-all cursor-pointer"
                                    onClick={handleExport}
                                >
                                    <span className="font-bold text-lg">Export :</span>
                                    <img
                                        src={excelPng}
                                        alt=""
                                        className="w-8"
                                    />
                                </div>
                            </div>

                            <div className='bg-white p-6 rounded-lg text-center max-w-[900px] m-auto shadow-md'>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <InfoItem label="Date de réalisation" value={moment(analysisData.date_realisation).format('DD/MM/YYYY')} />
                                    <InfoItem label="EC (µS/cm)" value={analysisData.ec} />
                                    <InfoItem label="Laboratoire" value={analysisData.laboratoire} />
                                    <InfoItem label="pH" value={analysisData.ph} />
                                    <InfoItem label="Échantillon" value={analysisData.echantillon} />
                                </div>

                                <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de l'eau</h2>
                                <table className="w-full border-collapse border border-gray-300 mb-4" id='waterAnalysisTable'>
                                    <thead className='table_heeader'>
                                        <tr>
                                            <th className="border border-gray-300 bg-gray-300 px-2 py-2">Paramètre</th>
                                            <th className="border border-gray-300 bg-gray-300 px-2 py-2"></th>
                                            <th className="border border-gray-300 bg-gray-300 px-2 py-2">mg/L</th>
                                            <th className="border border-gray-300 bg-gray-300 px-2 py-2">meq/L</th>
                                            <th className="border border-gray-300 bg-gray-300 px-2 py-2">Limite/Valeur</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {waterParameters.map(param => (
                                            <tr key={param.key} className={param.isHeader ? 'header_row' : ''}>
                                                {
                                                    param.isHeader ? (
                                                        <td colSpan="5" className="border border-gray-300 bg-gray-200 px-2 py-2 text-left font-semibold">{param.name}</td>
                                                    ) : (
                                                        <React.Fragment>
                                                            <td className="border border-gray-300 text-left px-2 py-2">{param.name}</td>
                                                            <td className="border border-gray-300 px-2 py-2">{param.symbol}</td>
                                                            <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_mg_l`]}</td>
                                                            <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_meq_l`]}</td>
                                                            <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_limval`]}</td>
                                                        </React.Fragment>
                                                    )
                                                }
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>


                            </div>
                        </>
                    )}

            </div>
        </div>
    );
};

export default ViewWaterAnalysis;

const InfoItem = ({ label, value }) => (
    <div>
        <span className="font-semibold">{label}: </span>
        <span>{value}</span>
    </div>
);